import authApi from 'apis/authApi';
import { useAppDispatch } from 'cache/store';
import LoadingIcon from 'components/loading/LoadingIcon';
import Register from 'components/register/register';
import { VERSION } from 'config/app.config';
import { DEVICE_INFO, KEY_HASH } from 'constants/common';
import { cacheUserInfo, login, rememberAccountAction, updateAgentType } from 'features/authSlice';
import { LoginPayload, RegisterPayload } from 'models/authModel';
import { encode } from 'punycode';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { getDeviceInfo, sha256Hash } from 'utils/function';
import { getRecaptchaToken } from 'utils/recaptcha';
import styles from './registerPage.style';

const RegisterPage = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    let { id } = useParams();

    const [loading, setLoading] = useState(false);

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [paymentPassword, setPaymentPassword] = useState('');
    const [nickname, setNickname] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');

    const [errors, setErrors] = useState({ username: '', password: '', paymentPassword: '', nickname: '', email: '', phone: '' });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        let formIsValid = true;
        const newErrors = { ...errors };

        // Kiểm tra username
        if (!username.trim()) {
            newErrors.username = 'Tên đăng nhập không được để trống';
            formIsValid = false;
        } else if (username.length < 5) {
            newErrors.username = 'Tên đăng nhập không được ít hơn 5 ký tự';
            formIsValid = false;
        } else {
            newErrors.username = '';
        }

        // Kiểm tra password
        if (!password.trim()) {
            newErrors.password = 'Mật khẩu đăng nhập không được để trống';
            formIsValid = false;
        } else if (password.length < 6) {
            newErrors.password = 'Mật khẩu đăng nhập không được ít hơn 6 ký tự';
            formIsValid = false;
        } else {
            newErrors.password = '';
        }

        // Kiểm tra paymentPassword
        if (!paymentPassword.trim()) {
            newErrors.paymentPassword = 'Mật khẩu thanh toán không được để trống';
            formIsValid = false;
        } else if (paymentPassword.length < 6) {
            newErrors.paymentPassword = 'Mật khẩu thanh toán không được ít hơn 6 ký tự';
            formIsValid = false;
        } else {
            newErrors.paymentPassword = '';
        }

        // Kiểm tra nickname
        if (!nickname.trim()) {
            newErrors.nickname = 'Biệt danh không được để trống';
            formIsValid = false;
        } else {
            newErrors.nickname = '';
        }

        // Kiểm tra email
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!email.trim()) {
            newErrors.email = 'Email không được để trống';
            formIsValid = false;
        } else if (!emailPattern.test(email)) {
            newErrors.email = 'Email không đúng định dạng';
            formIsValid = false;
        } else {
            newErrors.email = '';
        }

        // Kiểm tra phone
        const phonePattern = /^[0-9]{10}$/;
        if (!phone.trim()) {
            newErrors.phone = 'Số điện thoại không được để trống';
            formIsValid = false;
        } else if (!phonePattern.test(phone)) {
            newErrors.phone = 'Số điện thoại không được ít hơn 10 ký tự';
            formIsValid = false;
        } else {
            newErrors.phone = '';
        }

        setErrors(newErrors);

        if (formIsValid) {
            const recaptchaToken = await getRecaptchaToken('register');
            const payload = {
                agentid: '10020',
                id: id || '1635310',
                email: email,
                nickname: nickname,
                phone: phone,
                pwd: password,
                un: username,
                zjpwd: paymentPassword,
                loginname: username.toLowerCase(),
                enc: sha256Hash(`${username.toLowerCase()}${KEY_HASH}${password}${nickname}`),
            };
            if (recaptchaToken) {
                (payload as any).recaptchaToken = recaptchaToken;
            }
            onRegister(payload as any);
        }
    };

    const onRegister = async (values: RegisterPayload) => {
        try {
            const { data } = await authApi.signUp(values);
            if (!data.msg) {
                //thông báo đăng ký tài khoản thành công
                toast.success('Đăng ký tài khoản khoản thành công');

                let deviceInfo = DEVICE_INFO;
                if (!deviceInfo.uuid) {
                    deviceInfo = await getDeviceInfo();
                }

                //login account khi đăng ký tài khoản thành công
                const payloadLogin = {
                    username: values.un,
                    password: values.pwd,
                    version: VERSION,
                    di: deviceInfo,
                };

                //call login from header component
                submitLogin(payloadLogin, true, false);
            } else {
                toast.error(data.msg);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const getUserInfo = async (payload: LoginPayload, rememberAccount?: boolean) => {
        const { username, password } = payload;

        try {
            const { data } = await authApi.getUserInfo();
            if (!data.errorCode) {
                dispatch(cacheUserInfo(data));

                //cache agent type account
                let agentTypeUser = null;
                if (data.usertype === -2) {
                    agentTypeUser = 1;
                } else if (data['param9'] && data['param9'] !== '') {
                    agentTypeUser = 2;
                }
                dispatch(updateAgentType(agentTypeUser));

                //cache remember account
                if (rememberAccount) {
                    const rememberData = {
                        account: encode(`${username}|${password}`),
                        isRemember: true,
                    };
                    dispatch(rememberAccountAction(rememberData));
                } else {
                    dispatch(rememberAccountAction({ isRemember: false, account: null }));
                }
            }
        } catch (error) {
            console.log(error);
        }
    };

    const submitLogin = async (payload: LoginPayload, showAlertSuccess: boolean, rememberAccount?: boolean) => {
        try {
            const { data } = await authApi.loginGetToken(payload);
            if (data.msg === '') {
                //change state logged in store "auth slice"
                dispatch(login(data.data));

                if (showAlertSuccess) {
                    // toast.success('Đăng nhập thành công!');
                }

                navigate('/');

                getUserInfo(payload, rememberAccount);
            } else {
                toast.error(data.msg);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className={styles.container}>
                <div className={styles.viewForm}>
                    <img alt="" src={require('assets/images/reg/title-reg.png')} className={styles.image} />
                    <form className="containerInput" onSubmit={handleSubmit}>
                        <Register
                            placeholder="Tên đăng nhập"
                            img={require('assets/images/reg/icon-user.png')}
                            note="Không được ít hơn 5 ký tự và nhiều hơn 30 ký tự"
                            value={username}
                            onChangeText={setUsername}
                            error={errors.username}
                        />
                        <Register
                            placeholder="Mật khẩu đăng nhập"
                            img={require('assets/images/reg/icon-pasword.png')}
                            note="Không được giống mật khẩu thanh toán"
                            value={password}
                            onChangeText={setPassword}
                            error={errors.password}
                            typeInput="PASSWORD"
                        />
                        <Register
                            placeholder="Mật khẩu thanh toán"
                            img={require('assets/images/reg/icon-pasword.png')}
                            note="Không được giống mật khẩu đăng nhập"
                            value={paymentPassword}
                            onChangeText={setPaymentPassword}
                            error={errors.paymentPassword}
                            typeInput="PASSWORD"
                        />
                        <Register placeholder="Biệt danh" img={require('assets/images/reg/icon-nickname.png')} value={nickname} onChangeText={setNickname} error={errors.nickname} />
                        <Register placeholder="Email" img={require('assets/images/reg/icon-email.png')} value={email} onChangeText={setEmail} error={errors.email} />
                        <Register placeholder="Số điện thoại" img={require('assets/images/reg/icon-phone.png')} value={phone} onChangeText={setPhone} error={errors.phone} />
                        <button type="submit" className={styles.submitButton}>
                            Đăng ký
                        </button>
                    </form>
                </div>
            </div>
            {loading && <LoadingIcon />}
        </>
    );
};

export default RegisterPage;
